/**
 * Tiered notification sounds.
 *
 * `mention` — TNG doorbell ported from kritaka-electron, used when Akari
 * is @-mentioned. Loud enough to pull attention out of another tab.
 *
 * `low` — synthesized via Web Audio API, a soft single-tone tick for
 * ambient/non-mention notifications and (eventually) decision-arrived
 * pings. No audio file needed; volume stays well below the doorbell.
 *
 * Both helpers swallow playback errors — autoplay policies in some
 * browsers reject the very first sound until the user has interacted
 * with the page, and that's acceptable.
 */

const DOORBELL_URL = "/sounds/tng-doorbell.mp3";
const DOORBELL_VOLUME = 0.7;

let doorbell: HTMLAudioElement | null = null;
let audioCtx: AudioContext | null = null;

function getDoorbell(): HTMLAudioElement {
  if (!doorbell) {
    doorbell = new Audio(DOORBELL_URL);
    doorbell.volume = DOORBELL_VOLUME;
    doorbell.preload = "auto";
  }
  return doorbell;
}

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (audioCtx) return audioCtx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  audioCtx = new Ctor();
  return audioCtx;
}

export function playMentionSound(): void {
  try {
    const a = getDoorbell();
    a.currentTime = 0;
    void a.play().catch(() => {});
  } catch {
    /* autoplay or asset load failed; nothing actionable */
  }
}

export function playLowKeySound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(660, now);
  // Quick attack, brief sustain, exponential decay — feels like a soft "tick".
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.08, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.2);
}
